import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { Cookie, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useSite } from "@/lib/site";
import { readAnalyticsChoice, setAnalyticsChoice } from "@/lib/analytics";
import { useI18n } from "@/i18n";

/**
 * Cookie 說明與分析同意
 * ═══════════════════════════════════════════════════════════════
 *
 * 這裡混了兩件性質不同的事，分開講才不會誤導：
 *
 *   必要的 cookie   告知即可，不需要同意
 *   分析的 cookie   需要同意（歐盟與英國是事前同意）
 *
 * 本站的 cookie：
 *   cd_session          維持登入。HttpOnly、Secure、SameSite=Lax。必要
 *   colourdev.theme     亮版／暗版的選擇，存在瀏覽器本機。必要
 *   _ga、_ga_*          Google Analytics 4，量測流量。非必要
 *
 * 沒有廣告像素，也沒有把資料交給廣告產品 ——
 * index.html 裡 ad_storage 全地區一律 denied。
 *
 * ── 為什麼「拒絕」要和「接受」一樣好按 ─────────────────────
 *
 * 這一版之前只有「知道了」，因為當時所有 cookie 都是必要的，
 * 給選擇會是假的。加了 GA 之後情況變了：現在真的有東西可以拒絕，
 * 而 GDPR 要求拒絕不能比接受更麻煩（不能藏在第二層、不能是灰字）。
 *
 * 所以兩顆按鈕同樣大小、同樣位置。差別只在接受是主要樣式 ——
 * 那是允許的，藏起來才不允許。
 *
 * ── 為什麼關掉之後不再問 ────────────────────────────────────
 *
 * 選擇存在 localStorage，index.html 開頭會同步讀它並在 GA 載入前
 * 套用。用 cookie 記住「拒絕 cookie」是自打嘴巴，
 * 而 localStorage 不是 cookie，也不會跟著請求送出去。
 *
 * ── 為什麼用 localStorage 記住已讀 ─────────────────────────
 *
 * 用 cookie 記住「已經看過 cookie 告知」有點荒謬，
 * 而且那本身又是一個要揭露的 cookie。localStorage 不是 cookie，
 * 也不會跟著請求送出去。
 */

const STORAGE_KEY = "colouredev.cookie-notice";

export default function CookieNotice() {
  const { t } = useI18n();
  const { path } = useSite();

  /*
   * 初始一律不顯示。
   *
   * 直接用 localStorage 當初始值會在伺服器端渲染或第一幀時
   * 閃一下 —— 而「閃一下才消失」比從來沒出現過更擾人。
   */
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      if (window.localStorage.getItem(STORAGE_KEY)) return;
      // 已經在別的地方表達過意見的人（例如 /cookies 頁）不必再問一次
      if (readAnalyticsChoice()) return;
    } catch {
      // 無痕模式可能讀不到 localStorage。讀不到就顯示，寧可多顯示一次
    }

    // 延遲一點再出現：一進站就跳出來會蓋住使用者真正想看的東西
    const timer = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(timer);
  }, []);

  function close() {
    setVisible(false);
    try {
      window.localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    } catch {
      /* 存不了就下次再顯示一次，不是什麼大問題 */
    }
  }

  function choose(choice: "granted" | "denied") {
    setAnalyticsChoice(choice);
    close();
  }

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-x-3 bottom-3 z-50 sm:inset-x-auto sm:right-5 sm:bottom-5 sm:max-w-md"
          role="region"
          aria-label={t("cookie.aria")}
        >
          <div className="flex items-start gap-3.5 rounded-card nm-lg p-5">
            <span className="flex size-9 shrink-0 items-center justify-center rounded-xl nm-inset text-brand-800 dark:text-brand-400">
              <Cookie className="size-4" strokeWidth={1.9} />
            </span>

            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-ink">{t("cookie.title")}</p>
              <p className="mt-2 text-xs leading-relaxed text-ink-soft">
                {t("cookie.bodyA")}{" "}
                <strong className="text-ink">{t("cookie.bodyEmphasis")}</strong>{" "}
                {t("cookie.bodyB")}
              </p>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => choose("granted")}
                  className="rounded-xl nm-pressable px-4 py-2 text-xs font-medium text-ink"
                >
                  {t("cookie.accept")}
                </button>
                {/* 拒絕不能比接受更難按 —— 同樣大小、同樣位置 */}
                <button
                  type="button"
                  onClick={() => choose("denied")}
                  className="rounded-xl nm-pressable px-4 py-2 text-xs font-medium text-ink"
                >
                  {t("cookie.essential")}
                </button>
                <Link
                  to={path("/cookies")}
                  onClick={close}
                  className="px-1 text-xs font-medium text-brand-800 underline underline-offset-2 dark:text-brand-300"
                >
                  {t("cookie.details")}
                </Link>
              </div>
            </div>

            <button
              type="button"
              onClick={close}
              aria-label={t("common.close")}
              className="shrink-0 text-ink-muted transition-colors hover:text-ink-soft"
            >
              <X className="size-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
